import { FilterOperator } from '@nemuru-eng/js-api-client';
import { generatePath, useNavigate } from 'react-router-dom';

import { Customer } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/entities/CheckoutSessionDetailEntity';
import { useFetchCheckoutSessionList } from '@/components/modules/CheckoutSession/List/hooks/useFetchCheckoutSessionList';
import { checkoutSessionDataGridFields } from '@/components/modules/CheckoutSession/List/utils/getDataGridFields/checkoutSessionDataGridFields';
import { CheckoutSessionsStatusTag } from '@/components/shared/components/CheckoutSessionsStatusTag/CheckoutSessionsStatusTag';
import { LaunchButton } from '@/components/shared/components/LaunchButton';
import { MultiFilteredList } from '@/components/shared/components/MultiFilteredList';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import {
  ALLOWED_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER,
  INITIAL_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER,
} from '@/config/checkoutSessionConfig';

interface CheckoutSessionCustomerHistoryProps {
  customer: Customer;
}

export const CheckoutSessionCustomerHistory = ({ customer }: CheckoutSessionCustomerHistoryProps) => {
  const navigate = useNavigate();
  const { opened, setOpened, toggleDialog } = useDialog();

  const customerFullName = `${customer.fullName.firstName} ${customer.fullName.lastName}`;

  const { fetchList, fetchXLSXExport } = useFetchCheckoutSessionList({
    additionalFilters: customer.documentNumber
      ? [
          {
            field: 'document_number',
            operator: FilterOperator.IN,
            value: [customer.documentNumber],
          },
        ]
      : [],
  });

  return (
    <>
      <LaunchButton tId={'SEE_CLIENT_HISTORY'} onClick={toggleDialog} style={{ minWidth: 0, top: '-2px' }} />
      <NemuruDialog
        opened={opened}
        setOpened={setOpened}
        titleTextElement={<Text tId={'CLIENT_HISTORY'} dynamicDataTexts={[customerFullName]} />}
        dialogProps={{ maxWidth: 'lg' }}
      >
        <MultiFilteredList
          fetchList={fetchList}
          getXLSXExport={fetchXLSXExport}
          dataGridFields={checkoutSessionDataGridFields()}
          onRowClick={(selectedRow) => {
            navigate(generatePath(DYNAMIC_PAGE_PATHS.CHECKOUT_SESSION_DETAIL, { checkoutSessionId: selectedRow.id }));
          }}
          multiselectProps={{
            field: 'checkout_session_status',
            label: <Text tId={'STATUS'} />,
            defaultLabel: <Text tId={'DEFAULT_STATUSES'} />,
            items: ALLOWED_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER,
            checkBoxRender: (value) => {
              return <CheckoutSessionsStatusTag status={value} />;
            },
            defaultItems: INITIAL_CHECKOUT_SESSION_STATUSES_FOR_MULTI_SELECT_FILTER,
          }}
          dateFilterProps={{
            dateField: 'updated_at',
          }}
          defaultOrderBy={'updated_at'}
          showExport={false}
          showSearch={false}
          disableSelectionOnClick
        />
      </NemuruDialog>
    </>
  );
};
